import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";

const exits = [
  { xPercent: -105, yPercent: 0 },
  { xPercent: 0, yPercent: -105 },
  { xPercent: 0, yPercent: 105 },
  { xPercent: 105, yPercent: 0 },
];

export function Loader({ name, onComplete }) {
  const rootRef = useRef(null);
  const timelineRef = useRef(null);

  const skip = useCallback(() => {
    if (timelineRef.current) {
      timelineRef.current.kill();
      timelineRef.current = null;
    }
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === "Escape" || event.key === " " || event.key === "Enter") {
        skip();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [skip]);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const isTest = typeof navigator !== "undefined" && navigator.userAgent.toLowerCase().includes("jsdom");
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    if (isTest || reduceMotion) {
      onComplete();
      return undefined;
    }

    const panels = root.querySelectorAll(".loader__panel");
    const letters = root.querySelectorAll(".loader__letter");
    const logo = root.querySelector(".loader__logo");
    
    const timeline = gsap.timeline({
      defaults: { ease: "power3.inOut" },
      onComplete: () => {
        onComplete();
      },
    });
    timelineRef.current = timeline;

    const retractPanels = (position) => {
      exits.forEach((exit, index) => {
        timeline.to(
          panels[index],
          { ...exit, duration: 0.55, ease: "expo.inOut" },
          position + index * 0.025
        );
      });
    };

    gsap.set(letters, { yPercent: 115, opacity: 0 });
    timeline
      .to(letters, {
        yPercent: 0,
        opacity: 1,
        duration: 0.28,
        stagger: 0.025,
        ease: "power4.out",
      })
      .to({}, { duration: 0.25 })
      .set(panels, { opacity: 1 })
      .set(root, { backgroundColor: "transparent", pointerEvents: "none" })
      .to(logo, { yPercent: 120, opacity: 0, duration: 0.3, ease: "power3.in" }, "<");
    retractPanels(0.58);

    return () => {
      timeline.kill();
    };
  }, [onComplete]);

  return (
    <div className="loader" ref={rootRef} aria-hidden="true" onClick={skip} title="Click to skip">
      <div className="loader__panels">
        {[0, 1, 2, 3].map((panel) => (
          <span className={`loader__panel loader__panel--${panel + 1}`} key={panel} />
        ))}
      </div>
      <div className="loader__logo" aria-label={name}>
        {Array.from(name).map((letter, index) => (
          <span className="loader__letter" key={`${letter}-${index}`}>
            {letter === " " ? "\u00a0" : letter}
          </span>
        ))}
      </div>
    </div>
  );
}
